import AkFeatureGates from '@atlaskit/feature-gate-js-client';
import { defineMessages } from 'react-intl';

import { ChargeType, EditionType, PricingType, UiProductType } from '@adminhub/graphql-types';

import type { ProductConfigMapJson } from '../types/product-config-schema';
import { CollectionName } from '../types/product-config-schema';

const DEFAULT_PRODUCT_LINK = ({ productUrl }) => `https://${productUrl}?source=admin_hub`;
const DEFAULT_SETTINGS_LINK = ({ productUrl }) => `https://${productUrl}/settings?source=admin_hub`;

const AddProductMessages = defineMessages({
  jiraInfoTitle: {
    id: 'add-product-siteless.select-product-step.card.jiraInfoTitle',
    defaultMessage: 'Jira better connects teams and work for more seamless collaboration & faster alignment',
    description: 'Information about Jira',
  },
  jiraInfoSummaryOne: {
    id: 'add-product-siteless.select-product-step.card.jiraInfoSummaryOne',
    defaultMessage: 'Stronger cross-functional alignment and collaboration',
    description: 'Information about Jira',
  },
  jiraInfoSummaryTwo: {
    id: 'add-product-siteless.select-product-step.card.jiraInfoSummaryTwo',
    defaultMessage: 'Ease of use and efficiency improvements',
    description: 'Information about Jira',
  },
  jiraInfoSummaryThree: {
    id: 'add-product-siteless.select-product-step.card.jiraInfoSummaryThree',
    defaultMessage: 'Depth of developer benefits',
    description: 'Information about Jira',
  },
  bitbucketInfoTitle: {
    id: 'add-product-siteless.select-product-step.card.bitbucketInfoTitle',
    defaultMessage: 'Code & CI/CD, built for teams using Jira',
    description: 'Information about Bitbucket',
  },
  bitbucketInfoSummaryOne: {
    id: 'add-product-siteless.select-product-step.card.bitbucketInfoSummaryOne',
    defaultMessage: 'Collaborate across multiple teams',
    description: 'Information about Bitbucket',
  },
  bitbucketInfoSummaryTwo: {
    id: 'add-product-siteless.select-product-step.card.bitbucketInfoSummaryTwo',
    defaultMessage: 'Ship quality code with comprehensive code review',
    description: 'Information about Bitbucket',
  },
  bitbucketInfoSummaryThree: {
    id: 'add-product-siteless.select-product-step.card.bitbucketInfoSummaryThree',
    defaultMessage: 'Automate deployments with our built-in CI/CD tools',
    description: 'Information about Bitbucket',
  },
  confluenceInfoTitle: {
    id: 'add-product-siteless.select-product-step.card.confluenceInfoTitle',
    defaultMessage: 'Create, collaborate, and keep everything in one place',
    description: 'Information about Confluence',
  },
  confluenceInfoSummaryOne: {
    id: 'add-product-siteless.select-product-step.card.confluenceInfoSummaryOne',
    defaultMessage: 'Boost communication',
    description: 'Information about Confluence',
  },
  confluenceInfoSummaryTwo: {
    id: 'add-product-siteless.select-product-step.card.confluenceInfoSummaryTwo',
    defaultMessage: 'Eliminate silos',
    description: 'Information about Confluence',
  },
  confluenceInfoSummaryThree: {
    id: 'add-product-siteless.select-product-step.card.confluenceInfoSummaryThree',
    defaultMessage: 'Reduce context-switching',
    description: 'Information about Confluence',
  },
  jiraServiceManagementInfoTitle: {
    id: 'add-product-siteless.select-product-step.card.jiraServiceManagementInfoTitle',
    defaultMessage: 'Unlock development, operations, support and business teams with high-velocity ITSM',
    description: 'Information about Jira Service Management',
  },
  jiraServiceManagementInfoSummaryOne: {
    id: 'add-product-siteless.select-product-step.card.jiraServiceManagementInfoSummaryOne',
    defaultMessage: 'Deliver value fast',
    description: 'Information about Jira Service Management',
  },
  jiraServiceManagementInfoSummaryTwo: {
    id: 'add-product-siteless.select-product-step.card.jiraServiceManagementInfoSummaryTwo',
    defaultMessage: 'Make work visible',
    description: 'Information about Jira Service Management',
  },
  jiraServiceManagementInfoSummaryThree: {
    id: 'add-product-siteless.select-product-step.card.jiraServiceManagementInfoSummaryThree',
    defaultMessage: 'Connect Dev and Ops',
    description: 'Information about Jira Service Management',
  },
  jiraCustomerServiceInfoTitle: {
    id: 'add-product-siteless.select-product-step.card.jiraCustomerServiceInfoTitle.non-final',
    defaultMessage: 'Digital first, external customer support',
    description: 'Information about Jira Customer Service',
  },
  jiraCustomerServiceInfoSummaryOne: {
    id: 'add-product-siteless.select-product-step.card.jiraCustomerServiceInfoSummaryOne.non-final',
    defaultMessage: 'Deflect and manage customer support',
    description: 'Information about Jira Customer Service',
  },
  jiraCustomerServiceInfoSummaryTwo: {
    id: 'add-product-siteless.select-product-step.card.jiraCustomerServiceInfoSummaryTwo.non-final',
    defaultMessage: 'Reduce context-switching',
    description: 'Information about Jira Customer Service',
  },
  jiraCustomerServiceInfoSummaryThree: {
    id: 'add-product-siteless.select-product-step.card.jiraCustomerServiceInfoSummaryThree.non-final',
    defaultMessage: 'Connect Support, App and Development teams',
    description: 'Information about Jira Customer Service',
  },
  statuspageInfoTitle: {
    id: 'add-product-siteless.select-product-step.card.statuspageInfoTitle',
    defaultMessage: 'Keep your customers informed with automated status messages',
    description: 'Information about Statuspage',
  },
  statuspageInfoSummaryOne: {
    id: 'add-product-siteless.select-product-step.card.statuspageInfoSummaryOne',
    defaultMessage: 'Build customer trust',
    description: 'Information about Statuspage',
  },
  statuspageInfoSummaryTwo: {
    id: 'add-product-siteless.select-product-step.card.statuspageInfoSummaryTwo',
    defaultMessage: 'Reduce support requests',
    description: 'Information about Statuspage',
  },
  statuspageInfoSummaryThree: {
    id: 'add-product-siteless.select-product-step.card.statuspageInfoSummaryThree',
    defaultMessage: 'Scale incident processes',
    description: 'Information about Statuspage',
  },
  accessInfoSummaryOne: {
    id: 'add-product-siteless.select-product-step.card.accessInfoSummaryOne',
    defaultMessage: 'Enforce single sign-on and two-factor authentication',
    description: 'Information about Atlassian Access',
  },
  accessInfoSummaryTwo: {
    id: 'add-product-siteless.select-product-step.card.accessInfoSummaryTwo',
    defaultMessage: 'Control user and data security',
    description: 'Information about Atlassian Access',
  },
  accessInfoSummaryThree: {
    id: 'add-product-siteless.select-product-step.card.accessInfoSummaryThree',
    defaultMessage: 'Automate user lifecycle management',
    description: 'Information about Atlassian Access',
  },
  jiraWorkManagementInfoTitle: {
    id: 'add-product-siteless.select-product-step.card.jiraWorkManagementInfoTitle',
    defaultMessage: 'Let work flow, through every team in your organization',
    description: 'Information about Jira Work Management',
  },
  jiraWorkManagementInfoSummaryOne: {
    id: 'add-product-siteless.select-product-step.card.jiraWorkManagementInfoSummaryOne',
    defaultMessage: 'Confidence and control for business teams to track their work',
    description: 'Information about Jira Work Management',
  },
  jiraWorkManagementInfoSummaryTwo: {
    id: 'add-product-siteless.select-product-step.card.jiraWorkManagementInfoSummaryTwo',
    defaultMessage: 'View your work in the way that best works for you',
    description: 'Information about Jira Work Management',
  },
  jiraWorkManagementInfoSummaryThree: {
    id: 'add-product-siteless.select-product-step.card.jiraWorkManagementInfoSummaryThree',
    defaultMessage: 'Built on the same Jira you know and love',
    description: 'Information about Jira Work Management',
  },
  jiraProductDiscoveryInfoTitle: {
    id: 'add-product-siteless.select-product-step.card.jiraProductDiscoveryInfoTitle',
    defaultMessage: 'Rally everyone and build for impact',
    description: 'Information about jira Product Discovery',
  },
  jiraProductDiscoveryInfoSummaryOne: {
    id: 'add-product-siteless.select-product-step.card.jiraProductDiscoveryInfoSummaryOne',
    defaultMessage: 'Capture all app ideas, requests, data, and insights',
    description: 'Information about jira Product Discovery',
  },
  jiraProductDiscoveryInfoSummaryTwo: {
    id: 'add-product-siteless.select-product-step.card.jiraProductDiscoveryInfoSummaryTwo',
    defaultMessage: 'Prioritize easily and keep stakeholders engaged',
    description: 'Information about jira Product Discovery',
  },
  jiraProductDiscoveryInfoSummaryThree: {
    id: 'add-product-siteless.select-product-step.card.jiraProductDiscoveryInfoSummaryThree',
    defaultMessage: 'Enjoy discovery and delivery on the same platform',
    description: 'Information about jira Product Discovery',
  },
  loomInfoSummaryOne: {
    id: 'add-product-siteless.select-product-step.card.loomInfoSummaryOne',
    defaultMessage: 'Add context to anything',
    description: 'Information about Loom displayed on Loom product card',
  },
  loomInfoSummaryTwo: {
    id: 'add-product-siteless.select-product-step.card.loomInfoSummaryTwo',
    defaultMessage: 'Easily update your team without a meeting',
    description: 'Information about Loom displayed on Loom product card',
  },
  loomInfoSummaryThree: {
    id: 'add-product-siteless.select-product-step.card.loomInfoSummaryThree',
    defaultMessage: 'Quickly record your screen',
    description: 'Information about Loom displayed on Loom product card',
  },
  jiraAlignInfoSummaryOne: {
    id: 'add-product-siteless.select-product-step.card.jiraAlignInfoSummaryOne',
    defaultMessage: 'Make all work visible in real-time',
    description: 'Information about Jira Align displayed on Jira Align product card',
  },
  jiraAlignInfoSummaryTwo: {
    id: 'add-product-siteless.select-product-step.card.jiraAlignInfoSummaryTwo',
    defaultMessage: 'Align every team to strategy',
    description: 'Information about Jira Align displayed on Jira Align product card',
  },
  jiraAlignInfoSummaryThree: {
    id: 'add-product-siteless.select-product-step.card.jiraAlignInfoSummaryThree',
    defaultMessage: 'Keep everyone on course',
    description: 'Information about Jira Align displayed on Jira Align product card',
  },
  jiraProductDiscoveryDescription: {
    id: 'add-product-siteless.select-product-step.card.jiraProductDiscovery',
    defaultMessage: 'Dynamic app discovery',
    description: 'Information about jira Product Discovery',
  },
  jiraDescription: {
    id: 'add-product-siteless.select-product-step.card.jiraDescription',
    defaultMessage: 'Project management for any team',
    description: 'Description about Jira',
  },
  bitbucketDescription: {
    id: 'add-product-siteless.select-product-step.card.bitbucketDescription',
    defaultMessage: 'Git code management',
    description: 'Description about Bitbucket',
  },
  confluenceDescription: {
    id: 'add-product-siteless.select-product-step.card.confluenceDescription',
    defaultMessage: 'Document collaboration',
    description: 'Description about Confluence',
  },
  statusPageDescription: {
    id: 'add-product-siteless.select-product-step.card.statusPageDescription',
    defaultMessage: 'Incident communication',
    description: 'Description about Status Page',
  },
  accessDescription: {
    id: 'add-product-siteless.select-product-step.card.accessDescription',
    defaultMessage: 'Security controls for users, data and devices',
    description: 'Description about Atlassian Access',
  },
  jiraServiceManagementDescription: {
    id: 'add-product-siteless.select-product-step.card.jiraServiceManagementDescription',
    defaultMessage: 'High-velocity ITSM',
    description: 'Description about Jira Service Management',
  },
  jiraWorkManagementDescription: {
    id: 'add-product-siteless.select-product-step.card.jiraWorkManagementDescription',
    defaultMessage: 'Cross-team coordination',
    description: 'Description about Jira Work Management',
  },
  jiraCustomerServiceDescription: {
    id: 'add-product-siteless.select-product-step.card.jiraCustomerServiceDescription.non-final',
    defaultMessage: 'Digital first, external customer support',
    description: 'Description about Jira Customer Service',
  },
  loomDescription: {
    id: 'add-product-siteless.select-product-step.card.loomDescription',
    defaultMessage: 'Record once, share anywhere',
    description: 'Description about Loom displayed on Loom product card',
  },
  jiraAlignDescription: {
    id: 'add-product-siteless.select-product-step.card.jiraAlignDescription',
    defaultMessage: 'Unlock strategy and planning for your enterprise',
    description: 'Description about Jira Align displayed on Jira Align product card',
  },
  emptyDescription: {
    id: 'add-product-siteless.select-product-step.card.emptyDescription.no-translate',
    defaultMessage: 'Empty product description',
    description: 'Empty description for product cards that do not need a description',
  },
});

const appCardMessages = defineMessages({
  confluenceDescription: {
    id: 'add-collection.drawer.select-step.card.confluenceDescription',
    defaultMessage: 'Create documents, organize your work, and discuss everything in one place.',
    description: 'Description about Confluence',
  },
  jiraServiceManagementDescription: {
    id: 'add-collection.drawer.select-step.card.jiraServiceManagementDescription',
    defaultMessage: 'Deliver responsive and reliable IT service management experiences for customers and employees, fast.',
    description: 'Description about Jira Service Management',
  },
  jiraCustomerServiceDescription: {
    id: 'add-collection.drawer.select-step.card.jiraCustomerServiceDescription',
    defaultMessage: 'Connect teams in support, product, development, and operations to deliver high-velocity customer service.',
    description: 'Description about Jira Customer Service',
  },
  jiraDescription: {
    id: 'add-collection.drawer.select-step.card.jiraDescription',
    defaultMessage: 'Plan, track, and deliver ideas with the project management tool for all teams.',
    description: 'Description about Jira',
  },
  statusPageDescription: {
    id: 'add-collection.drawer.select-step.card.statusPageDescription',
    defaultMessage: 'Incidents happen. Keep your users informed and ditch the flood of support emails during downtime.',
    description: 'Description about Statuspage',
  },
  jiraProductDiscoveryDescription: {
    id: 'add-collection.drawer.select-step.card.jiraProductDiscoveryDescription',
    defaultMessage: 'Organize and prioritize ideas, share product roadmaps, and connect business and tech teams in Jira.',
    description: 'Description about Jira Product Discovery',
  },
  accessDescription: {
    id: 'add-collection.drawer.select-step.card.accessDescription',
    defaultMessage: 'Get company-wide visibility, security policies, and control across your Atlassian cloud infrastructure.',
    description: 'Description about Atlassian Guard',
  },
  bitbucketDescription: {
    id: 'add-collection.drawer.select-step.card.bitbucketDescription',
    defaultMessage: 'Collaborate on code with pull requests, manage Git repos, and confidently build and ship software, as a team.',
    description: 'Description about Bitbucket',
  },
  // TODO: confirm description for Jira Work Management
  jiraWorkManagementDescription: {
    id: 'add-collection.drawer.select-step.card.jiraWorkManagementDescription',
    defaultMessage: 'Cross-team coordination ',
    description: 'Description about Jira Work Management',
  },
  loomDescription: {
    id: 'add-collection.drawer.select-step.card.loomDescription',
    defaultMessage: 'Record and share quick, async video updates to align teams working together and apart.',
    description: 'Description about Loom',
  },
  // TODO: confirm description for Jira Align
  jiraAlignDescription: {
    id: 'add-collection.drawer.select-step.card.jiraAlignDescription',
    defaultMessage: 'Unlock strategy and planning for your enterprise.',
    description: 'Description about Jira Align',
  },
  rovoDevDescription: {
    id: 'add-collection.drawer.select-step.card.rovoDevDescription.non-final',
    defaultMessage: "Use AI to generate code, review changes, and accelerate your team's development work.",
    description: 'Description about Rovo Dev',
  },
  feedbackDescription: {
    id: 'add-collection.drawer.select-step.card.feedbackDescription',
    defaultMessage: 'Capture, organize, and act on user feedback to build better products.',
    description: 'Description about Feedback',
  },
  emptyDescription: {
    id: 'add-collection.drawer.select-step.card.emptyDescription.no-translate',
    defaultMessage: 'Add Passionfruit to your organization.',
    description: 'Add App description for Passionfruit. Needs to be updated later.',
  },
});

const thirdPartyAppMessages = defineMessages({
  adobeSignDisplayName: {
    id: 'product-config.adobe-sign.display-name',
    defaultMessage: 'Adobe Sign',
    description: 'Display name for 3rd party app - Adobe Sign',
  },
  ahaDisplayName: {
    id: 'product-config.aha.display-name',
    defaultMessage: 'Aha!',
    description: 'Display name for 3rd party app - Aha!',
  },
  airtableDisplayName: {
    id: 'product-config.airtable.display-name',
    defaultMessage: 'Airtable',
    description: 'Display name for 3rd party app - Airtable',
  },
  asanaDisplayName: {
    id: 'product-config.asana.display-name',
    defaultMessage: 'Asana',
    description: 'Display name for 3rd party app - Asana',
  },
  azureDevOpsDisplayName: {
    id: 'product-config.azure-devops.display-name',
    defaultMessage: 'Azure DevOps',
    description: 'Display name for 3rd party app - Azure DevOps',
  },
  boxDisplayName: {
    id: 'product-config.box.display-name',
    defaultMessage: 'Box',
    description: 'Display name for 3rd party app - Box',
  },
  canvaDisplayName: {
    id: 'product-config.canva.display-name',
    defaultMessage: 'Canva',
    description: 'Display name for 3rd party app - Canva',
  },
  confluenceDcDisplayName: {
    id: 'product-config.confluence-dc.display-name',
    defaultMessage: 'Confluence Data Center',
    description: 'Display name for 3rd party app - Confluence Data Center',
  },
  databricksDisplayName: {
    id: 'product-config.databricks.display-name',
    defaultMessage: 'Databricks',
    description: 'Display name for 3rd party app - Databricks',
  },
  datadogDisplayName: {
    id: 'product-config.datadog.display-name',
    defaultMessage: 'Datadog',
    description: 'Display name for 3rd party app - Datadog',
  },
  docusignDisplayName: {
    id: 'product-config.docusign.display-name',
    defaultMessage: 'Docusign',
    description: 'Display name for 3rd party app - Docusign',
  },
  dropboxDisplayName: {
    id: 'product-config.dropbox.display-name',
    defaultMessage: 'Dropbox',
    description: 'Display name for 3rd party app - Dropbox',
  },
  githubDisplayName: {
    id: 'product-config.github.display-name',
    defaultMessage: 'GitHub',
    description: 'Display name for 3rd party app - GitHub',
  },
  gitlabDisplayName: {
    id: 'product-config.gitlab.display-name',
    defaultMessage: 'GitLab',
    description: 'Display name for 3rd party app - GitLab',
  },
  gmailDisplayName: {
    id: 'product-config.gmail.display-name',
    defaultMessage: 'Gmail',
    description: 'Display name for 3rd party app - Gmail',
  },
  googleCalendarDisplayName: {
    id: 'product-config.google-calendar.display-name',
    defaultMessage: 'Google Calendar',
    description: 'Display name for 3rd party app - Google Calendar',
  },
  googleDriveDisplayName: {
    id: 'product-config.google-drive.display-name',
    defaultMessage: 'Google Drive',
    description: 'Display name for 3rd party app - Google Drive',
  },
  hubspotDisplayName: {
    id: 'product-config.hubspot.display-name',
    defaultMessage: 'HubSpot',
    description: 'Display name for 3rd party app - HubSpot',
  },
  jiraDcDisplayName: {
    id: 'product-config.jira-dc.display-name',
    defaultMessage: 'Jira Data Center',
    description: 'Display name for 3rd party app - Jira Data Center',
  },
  lucidDisplayName: {
    id: 'product-config.lucid.display-name',
    defaultMessage: 'Lucid',
    description: 'Display name for 3rd party app - Lucid',
  },
  microsoftOneDriveDisplayName: {
    id: 'product-config.microsoft-one-drive.display-name',
    defaultMessage: 'Microsoft OneDrive',
    description: 'Display name for 3rd party app - Microsoft OneDrive',
  },
  microsoftOutlookCalendarDisplayName: {
    id: 'product-config.microsoft-outlook-calendar.display-name',
    defaultMessage: 'Microsoft Outlook Calendar',
    description: 'Display name for 3rd party app - Microsoft Outlook Calendar',
  },
  microsoftOutlookMailDisplayName: {
    id: 'product-config.microsoft-outlook-mail.display-name',
    defaultMessage: 'Microsoft Outlook Mail',
    description: 'Display name for 3rd party app - Microsoft Outlook Mail',
  },
  microsoftSharepointDisplayName: {
    id: 'product-config.microsoft-sharepoint.display-name',
    defaultMessage: 'Microsoft SharePoint',
    description: 'Display name for 3rd party app - Microsoft SharePoint',
  },
  microsoftTeamsDisplayName: {
    id: 'product-config.microsoft-teams.display-name',
    defaultMessage: 'Microsoft Teams',
    description: 'Display name for 3rd party app - Microsoft Teams',
  },
  miroDisplayName: {
    id: 'product-config.miro.display-name',
    defaultMessage: 'Miro',
    description: 'Display name for 3rd party app - Miro',
  },
  mondayDisplayName: {
    id: 'product-config.monday.display-name',
    defaultMessage: 'Monday',
    description: 'Display name for 3rd party app - Monday',
  },
  newRelicDisplayName: {
    id: 'product-config.new-relic.display-name',
    defaultMessage: 'NewRelic',
    description: 'Display name for 3rd party app - NewRelic',
  },
  notionDisplayName: {
    id: 'product-config.notion.display-name',
    defaultMessage: 'Notion',
    description: 'Display name for 3rd party app - Notion',
  },
  salesforceDisplayName: {
    id: 'product-config.salesforce.display-name',
    defaultMessage: 'Salesforce',
    description: 'Display name for 3rd party app - Salesforce',
  },
  serviceNowDisplayName: {
    id: 'product-config.service-now.display-name',
    defaultMessage: 'ServiceNow',
    description: 'Display name for 3rd party app - ServiceNow',
  },
  slackDisplayName: {
    id: 'product-config.slack.display-name',
    defaultMessage: 'Slack',
    description: 'Display name for 3rd party app - Slack',
  },
  smartsheetDisplayName: {
    id: 'product-config.smartsheet.display-name',
    defaultMessage: 'Smartsheet',
    description: 'Display name for 3rd party app - Smartsheet',
  },
  spinnakerDisplayName: {
    id: 'product-config.spinnaker.display-name',
    defaultMessage: 'Spinnaker',
    description: 'Display name for 3rd party app - Spinnaker',
  },
  workdayDisplayName: {
    id: 'product-config.workday.display-name',
    defaultMessage: 'Workday',
    description: 'Display name for 3rd party app - Workday',
  },
  workdayV2DisplayName: {
    id: 'product-config.workday-v2.display-name',
    defaultMessage: 'Workday V2',
    description: 'Display name for 3rd party app - Workday V2',
  },
  zendeskDisplayName: {
    id: 'product-config.zendesk.display-name',
    defaultMessage: 'Zendesk',
    description: 'Display name for 3rd party app - Zendesk',
  },
});

export const config: ProductConfigMapJson = {
  products: {
    access: {
      ariResourceOwner: 'access',
      hamsProductKey: undefined,
      productName: 'Atlassian Guard',
      productSettingName: undefined, // AdminHub is the settings page for Access
      type: UiProductType.ACCESS,
      description: AddProductMessages.accessDescription,
      summaryPoints: [
        AddProductMessages.accessInfoSummaryOne,
        AddProductMessages.accessInfoSummaryTwo,
        AddProductMessages.accessInfoSummaryThree,
      ],
      addProduct: {
        enabled: true,
        productKey: 'access',
        includeWithAccess: true,
        productCard: {
          learnMoreUrl: 'https://www.atlassian.com/software/access',
          appCardDescription: appCardMessages.accessDescription,
        },
      },
      alternatives: {
        billingProductKey: ['access'],
        productName: ['access', 'Access', 'Atlassian Access', 'Atlassian Guard', 'Guard'],
        userManagementProductId: ['com.atlassian.identity-manager'],
        userManagementCanonicalProductKey: ['com.atlassian.identity-manager'],
      },
      productId: 'ad8473d4-3a4d-4e36-827c-0a893439e9fa',
      offerings: {
        '1ac3b7dd-7141-414e-bc00-c890acc458b0': {
          productKey: 'com.atlassian.identity-manager',
          chargeElement: ChargeType.user,
        },
      },
      productIcon: 'GuardIcon',
      productLogo: 'GuardLogo',
    },
    'adobe.sign': {
      ariResourceOwner: 'adobe.sign',
      productName: thirdPartyAppMessages.adobeSignDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.adobeSignDisplayName,
      connectorType: 'adobe-sign-connector',
      productIcon: 'adobeSignIcon',
    },
    'aha.aha': {
      ariResourceOwner: 'aha.aha',
      productName: thirdPartyAppMessages.ahaDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.ahaDisplayName,
      connectorType: 'aha-connector',
      productIcon: 'ahaIcon',
    },
    'airtable.airtable': {
      ariResourceOwner: 'airtable.airtable',
      productName: thirdPartyAppMessages.airtableDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.airtableDisplayName,
      connectorType: 'airtable-connector',
      productIcon: 'airtableIcon',
    },
    'asana.asana': {
      ariResourceOwner: 'asana.asana',
      productName: thirdPartyAppMessages.asanaDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.asanaDisplayName,
      connectorType: 'asana-connector',
      productIcon: 'asanaIcon',
    },
    'atlassian-together': {
      ariResourceOwner: 'atlassian-together',
      productName: 'Atlassian Together',
      alternatives: {
        productName: ['atlassian-together', 'Atlassian Together'],
      },
      emauProductKey: 'atlassian_together',
      hamsProductKey: undefined,
    },
    avocado: {
      ariResourceOwner: 'avocado',
      type: UiProductType.AVOCADO,
      productName: 'Avocado',
      alternatives: {
        productName: ['avocado', 'Avocado'],
      },
      emauProductKey: undefined,
      hamsProductKey: undefined,
      links: {
        productLink: DEFAULT_PRODUCT_LINK,
        settingsLink: DEFAULT_SETTINGS_LINK,
      },
    },
    avp: {
      ariResourceOwner: 'avp',
      type: UiProductType.ANALYTICS,
      productName: 'Atlassian Analytics',
      userManagementProductId: 'avp',
      idOrgManagerProductKey: 'avp',
      emauProductKey: 'atlassian_analytics',
      userManagementCanonicalProductKey: 'avp',
      sandboxProductKey: undefined,
      hamsProductKey: undefined,
      productIcon: 'AtlassianAnalyticsIcon',
      alternatives: {
        idOrgManagerProductKey: ['analytics', 'atlassian-analytics-free'],
        productName: ['avp', 'atlassian-analytics-free', 'Analytics'],
        userManagementProductId: ['atlassian-analytics-free'],
      },
      links: {
        productLink: ({ productUrl }) => `https://${productUrl}/?source=admin_hub`,
        settingsLink: ({ productUrl }) => `https://${productUrl}/settings/?source=admin_hub`,
      },
      productId: 'f44da23e-5907-4d81-8a78-33bedee10969',
    },
    'azure-devops.azure-devops': {
      ariResourceOwner: 'azure-devops.azure-devops',
      productName: thirdPartyAppMessages.azureDevOpsDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.azureDevOpsDisplayName,
      connectorType: 'azure-devops-connector',
      productIcon: 'azureDevopsIcon',
    },
    beacon: {
      ariResourceOwner: 'beacon',
      productName: 'Guard Detect',
      hamsProductKey: undefined,
      productIcon: 'GuardIcon',
      productLogo: 'GuardLogo',
      alternatives: {
        productName: ['beacon', 'Beacon'],
        billingProductKey: ['guardPremium'],
      },
      links: {
        productLink: DEFAULT_PRODUCT_LINK,
        settingsLink: ({ productUrl }) => `https://${productUrl}/siem-forwarding?source=admin_hub`,
      },
      productId: '0a0a31b0-3f5f-4693-b231-9a753bee012e',
      offerings: {
        '2f5e5808-d501-450a-b246-b0bd50a0b72a': {
          productKey: 'com.atlassian.guard-premium',
          chargeElement: ChargeType.product_user,
        },
      },
    },
    bitbucket: {
      ariResourceOwner: 'bitbucket',
      ariResourceType: 'workspace',
      productName: 'Bitbucket',
      hamsProductKey: undefined,
      productLogo: 'BitbucketLogo',
      productIcon: 'BitbucketIcon',
      dcProductIcon: 'BitbucketDataCenterIcon',
      type: UiProductType.BITBUCKET,
      billingProductKey: 'Bitbucket',
      productId: 'ad1076cd-0b3f-4e57-8f46-3dcd70ee259f',
      alternatives: {
        productName: ['bitbucket'],
        idOrgManagerProductKey: ['com.atlassian.bitbucket'],
        productId: ['3ce9ed1f-0561-484f-9e7a-e47efa3c0156', '7b4bb2aa-e04f-44cb-b9ae-e752d28239e3'],
      },
      offerings: {
        'a2eff1c5-3d31-4e3c-b483-d28cc2b6592d': {
          productKey: 'com.atlassian.bitbucket',
          chargeElement: ChargeType.user,
          edition: EditionType.free,
          pricingType: PricingType.FREE,
        },
      },
      description: AddProductMessages.bitbucketDescription,
      summaryPoints: [
        AddProductMessages.bitbucketInfoSummaryOne,
        AddProductMessages.bitbucketInfoSummaryTwo,
        AddProductMessages.bitbucketInfoSummaryThree,
      ],
      addProduct: {
        enabled: true,
        productKey: 'bitbucket',
        includeWithVortex: true,
        productCard: {
          learnMoreUrl: 'https://www.atlassian.com/software/bitbucket',
          summaryTitle: AddProductMessages.bitbucketInfoTitle,
          appCardDescription: appCardMessages.bitbucketDescription,
        },
      },
      links: {
        productLink: ({ productUrl }) => `https://${productUrl}workspace/overview/?source=admin_hub`,
        settingsLink: ({ productUrl }) => `https://${productUrl}workspace/settings/?source=admin_hub`,
      },
      signUpLink: 'https://id.atlassian.com/signup?application=bitbucket',
      wacUrlPath: ({ siteName, productKey, cloudId, requestId }) =>
        `/ondemand/signup/confirmation?ondemandurl=${siteName}&products=${productKey}&cloudId=${cloudId}&requestId=${requestId}`,
      ccpWacUrlPath: ({ platform, provisionRequestId, ondemandUrl, productKey }) =>
        `/ondemand/signup/confirmation?platform=${platform}&provisioningRequestId=${provisionRequestId}&ondemandurl=${ondemandUrl}&products=${productKey}`,
    },
    'box.box': {
      ariResourceOwner: 'box.box',
      productName: thirdPartyAppMessages.boxDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.boxDisplayName,
      connectorType: 'box-connector',
      productIcon: 'boxIcon',
    },
    'canva.canva': {
      ariResourceOwner: 'canva.canva',
      productName: thirdPartyAppMessages.canvaDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.canvaDisplayName,
      connectorType: 'canva-connector',
      productIcon: 'canvaIcon',
    },
    canvas: {
      ariResourceOwner: 'canvas',
      type: UiProductType.FABRIC,
      productName: 'Fabric',
      userManagementProductId: 'canvas',
      idOrgManagerProductKey: 'canvas',
      userManagementCanonicalProductKey: 'canvas',
      alternatives: {
        productName: ['canvas', 'atlassian-whiteboard', 'Fabric'],
      },
      sandboxProductKey: 'canvas',
      emauProductKey: 'fabric',
      hamsProductKey: undefined,
      links: {
        productLink: ({ productUrl }) => `https://${productUrl}/?source=admin_hub`,
        // Fabric has product link as settings link
        settingsLink: ({ productUrl }) => `https://${productUrl}/?source=admin_hub`,
      },
    },
    'cloud-admin': {
      ariResourceOwner: 'cloud-admin',
      productName: 'Atlassian Administration',
      alternatives: {
        productName: ['cloud-admin'],
      },
      emauProductKey: undefined,
      sandboxProductKey: undefined,
      hamsProductKey: undefined,
      productIcon: 'AtlassianAdministrationIcon',
      productLogo: 'AtlassianAdministrationLogo',
    },
    cmdb: {
      ariResourceOwner: 'cmdb',
      productName: 'Assets',
      type: UiProductType.ASSETS,
      hamsProductKey: undefined,
      productIcon: 'AssetsIcon',
      productLogo: 'AssetsLogo',
      productId: 'assets',
      idOrgManagerProductKey: 'assets',
      alternatives: {
        productName: ['assets'],
        idOrgManagerProductKey: ['assets', 'cmdb'],
      },
    },
    compass: {
      ariResourceOwner: 'compass',
      productName: 'Compass',
      alternatives: {
        productName: ['compass'],
      },
      hamsProductKey: undefined,
      productLogo: 'CompassLogo',
      productIcon: 'CompassIcon',
      links: {
        productLink: DEFAULT_PRODUCT_LINK,
        settingsLink: undefined,
      },
      productId: '6bce2ccb-4f57-46f5-812a-9dbf02dc28f2',
    },
    confluence: {
      ariResourceOwner: 'confluence',
      productName: 'Confluence',
      userManagementProductId: 'conf',
      sandboxProductKey: 'confluence.ondemand',
      hamsProductKey: 'confluence.ondemand',
      alternatives: {
        productName: ['confluence'],
        idOrgManagerProductKey: ['confluence', 'confluence.ondemand'],
        userManagementProductId: ['confluence', 'confluence.ondemand'],
        userManagementCanonicalProductKey: ['confluence', 'confluence.ondemand'],
      },
      productLogo: 'ConfluenceLogo',
      productIcon: 'ConfluenceIcon',
      dcProductIcon: 'ConfluenceDataCenterIcon',
      enterpriseProductKey: 'com.atlassian.enterprise-confluence',
      links: {
        productLink: ({ productUrl }) => `https://${productUrl}/?source=admin_hub`,
        settingsLink: ({ productUrl }) => `https://${productUrl}/admin/configuration?source=admin_hub`,
        manageAppsLink: ({ productUrl }) => `https://${productUrl}/plugins/servlet/upm?source=admin_hub`,
        manageGuestAccessLink: ({ productUrl, users, groups }) =>
          `https://${productUrl}/admin/permissions/global?tab=external${
            users?.length ? `&addSpaceAccess=true&users=${users?.join()}` : ''
          }${groups?.length ? `&groups=${groups?.join()}` : ''}`,
      },
      signUpLink: 'https://www.atlassian.com/try/cloud/signup?bundle=confluence',
      productId: '6aab3f4e-cc71-474d-8d28-abef8b057808',
      offerings: {
        '2eda1119-fab4-4699-ab37-ff8f12052cde': {
          productKey: 'confluence.ondemand',
          chargeElement: ChargeType.user,
          edition: EditionType.standard,
          pricingType: PricingType.PAID,
        },
        '39605741-b92f-4763-8229-7bba2d16433c': {
          productKey: 'confluence.ondemand',
          chargeElement: ChargeType.user,
          edition: EditionType.enterprise,
          pricingType: PricingType.FREE,
        },
        '9d6c4ae6-1706-4ca0-b0c0-c1609937523b': {
          productKey: 'confluence.ondemand',
          chargeElement: ChargeType.user,
          edition: EditionType.enterprise,
          pricingType: PricingType.FREE,
          parentOfferingId: 'cf0b0103-2f08-4c87-97cf-a69448d6e69a',
        },
        'd7ee0587-a526-435e-b108-8ac020070545': {
          productKey: 'confluence.ondemand',
          chargeElement: ChargeType.user,
          edition: EditionType.enterprise,
          pricingType: PricingType.FREE,
          environment: 'fedramp',
        },
        'ebe68aa1-7235-469a-a215-211c251d83f2': {
          productKey: 'confluence.ondemand',
          chargeElement: ChargeType.user,
          edition: EditionType.enterprise,
          pricingType: PricingType.FREE,
          environment: 'ic',
        },
      },
      description: AddProductMessages.confluenceDescription,
      summaryPoints: [
        AddProductMessages.confluenceInfoSummaryOne,
        AddProductMessages.confluenceInfoSummaryTwo,
        AddProductMessages.confluenceInfoSummaryThree,
      ],
      addProduct: {
        enabled: true,
        includeWithTWCEnterprise: true,
        productKey: 'confluence.ondemand',
        parentCollectionName: CollectionName.TeamworkCollection,
        productCard: {
          learnMoreUrl: 'https://www.atlassian.com/software/confluence',
          summaryTitle: AddProductMessages.confluenceInfoTitle,
          appCardDescription: appCardMessages.confluenceDescription,
        },
        vortexOnly: false,
        nonVortexOnly: false,
        enterpriseProductKey: 'confluence.ondemand',
        welcomePath: 'wiki?src=signup&admin=true',
      },
    },
    'confluence-dc': {
      ariResourceOwner: 'confluence-dc',
      productName: thirdPartyAppMessages.confluenceDcDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.confluenceDcDisplayName,
      connectorType: 'confluence-dc-connector',
      productIcon: 'ConfluenceIcon',
    },
    'databricks.databricks': {
      ariResourceOwner: 'databricks.databricks',
      productName: thirdPartyAppMessages.databricksDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.databricksDisplayName,
      connectorType: 'databricks-connector',
      productIcon: 'databricksIcon',
    },
    'datadog.datadog': {
      ariResourceOwner: 'datadog.datadog',
      productName: thirdPartyAppMessages.datadogDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.datadogDisplayName,
      connectorType: 'datadog-connector',
      productIcon: 'datadogIcon',
    },
    devai: {
      ariResourceOwner: 'devai',
      alternatives: {
        productName: ['Devai', 'Dev AI', 'devai.ondemand'],
        idOrgManagerProductKey: ['devai.ondemand'],
        userManagementCanonicalProductKey: ['devai.ondemand'],
        userManagementProductId: ['devai.ondemand'],
      },
      billingProductKey: 'devai.ondemand',
      hamsProductKey: 'devai.ondemand',
      productName: 'Rovo Dev',
      productSettingName: 'Dev AI',
      productLogo: 'RovoDevLogo',
      productIcon: 'RovoDevIcon',
      productId: '56cce7cb-8c54-4103-8736-e64d1ab00128',
      type: UiProductType.DEVAI,
      description: AddProductMessages.emptyDescription,
      summaryPoints: [],
      addProduct: {
        enabled: true,
        productKey: 'devai.ondemand',
        productCard: {
          learnMoreUrl: 'https://www.atlassian.com/software/rovo-dev',
          appCardDescription: appCardMessages.rovoDevDescription,
        },
      },
      offerings: {
        '7618c3e4-2e84-4a47-b322-d2515835c55a': {
          productKey: 'devai.ondemand',
          chargeElement: ChargeType.user,
          edition: EditionType.standard,
          pricingType: PricingType.PAID,
        },
      },
      links: {
        productLink: ({ productUrl }) => `https://${productUrl}/`,
        settingsLink: ({ productUrl }) => `https://${productUrl}/site-settings`,
        manageAISettingsLink: ({ orgId }) => `/o/${orgId}/ai-enabled-apps/rovo-dev`,
        manageCreditsLink: ({ orgId, usageKey, ccpEntitlementId }) => `/o/${orgId}/platform-usage/${usageKey}_${ccpEntitlementId}`,
      },
    },
    'docusign.docusign': {
      ariResourceOwner: 'docusign.docusign',
      productName: thirdPartyAppMessages.docusignDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.docusignDisplayName,
      connectorType: 'docusign-connector',
      productIcon: 'docusignIcon',
    },
    dropbox: {
      ariResourceOwner: 'dropbox',
      productName: thirdPartyAppMessages.dropboxDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.dropboxDisplayName,
      connectorType: 'dropbox-connector',
      productIcon: 'dropboxIcon',
    },
    feedback: {
      ariResourceOwner: 'feedback',
      productName: 'Feedback',
      hamsProductKey: undefined,
    },
    'github.github': {
      ariResourceOwner: 'github.github',
      productName: thirdPartyAppMessages.githubDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.githubDisplayName,
      connectorType: 'github-connector',
      productIcon: 'githubIcon',
    },
    'gitlab.gitlab': {
      ariResourceOwner: 'gitlab.gitlab',
      productName: thirdPartyAppMessages.gitlabDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.gitlabDisplayName,
      connectorType: 'gitlab-connector',
      productIcon: 'gitlabIcon',
    },
    goal: {
      ariResourceOwner: 'goal',
      type: UiProductType.GOAL,
      productName: 'Goals',
      alternatives: {
        productName: ['Goal'],
      },
      hamsProductKey: undefined,
      productSettingName: undefined, // AdminHub is the settings page for Goals
      links: {
        productLink: ({ homeUrl, orgId, siteId }) => `${homeUrl}/o/${orgId}/s/${siteId}/goals`,
        settingsLink: ({ homeUrl, orgId, siteId }) => `${homeUrl}/o/${orgId}/s/${siteId}/goals/settings`,
      },
      productIcon: 'GoalsIcon',
      productLogo: 'GoalsLogo',
      productId: '8a7a2515-a0c8-4866-9711-8fe51ccb2627',
    },
    'google.gmail': {
      ariResourceOwner: 'google.gmail',
      productName: thirdPartyAppMessages.gmailDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.gmailDisplayName,
      connectorType: 'gmail-connector',
      productIcon: 'gmailIcon',
    },
    'google.google-calendar': {
      ariResourceOwner: 'google.google-calendar',
      productName: thirdPartyAppMessages.googleCalendarDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.googleCalendarDisplayName,
      connectorType: 'google-calendar-connector',
      productIcon: 'googleCalendarIcon',
    },
    'google.google-drive': {
      ariResourceOwner: 'google.google-drive',
      productName: thirdPartyAppMessages.googleDriveDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.googleDriveDisplayName,
      connectorType: 'platform-google-drive-connector',
      productIcon: 'googleDriveIcon',
    },
    'hipchat.cloud': {
      ariResourceOwner: 'hipchat.cloud',
      type: UiProductType.STRIDE,
      productName: 'Stride',
      userManagementProductId: 'hipchat.cloud',
      userManagementCanonicalProductKey: 'chat',
      idOrgManagerProductKey: 'stride',
      sandboxProductKey: 'chat',
      emauProductKey: 'stride',
      hamsProductKey: undefined,
    },
    'hubspot.hubspot': {
      ariResourceOwner: 'hubspot.hubspot',
      productName: thirdPartyAppMessages.hubspotDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.hubspotDisplayName,
      connectorType: 'hubspot-connector',
      productIcon: 'hubspotIcon',
    },
    jira: {
      ariResourceOwner: 'jira',
      type: UiProductType.JIRA_ADMIN,
      productName: 'Jira Administration',
      userManagementProductId: 'jira-admin',
      userManagementCanonicalProductKey: undefined,
      idOrgManagerProductKey: undefined,
      alternatives: {
        productName: ['jira-admin', 'Jira Administration'],
        idOrgManagerProductKey: ['jira-admin'],
      },
      sandboxProductKey: undefined,
      emauProductKey: undefined,
      hamsProductKey: undefined,
      productIcon: 'JiraIcon',
      productLogo: 'JiraLogo',
      productSettingName: 'Jira', // All Jira products share the same settings page
      links: {
        productLink: ({ productUrl }) => `https://${productUrl}/secure/Dashboard.jspa?source=admin_hub`,
        settingsLink: ({ productUrl }) => {
          const useNewSettingsPage = AkFeatureGates.checkGate('mc-adminhub-product-config-jira-links');

          return useNewSettingsPage
            ? `https://${productUrl}/jira/settings/system/general-configuration?source=admin_hub`
            : `https://${productUrl}/secure/admin/ViewApplicationProperties.jspa?source=admin_hub`;
        },
        manageAppsLink: ({ productUrl }) => `https://${productUrl}/plugins/servlet/upm?source=admin_hub`,
      },
    },
    'jira-align': {
      ariResourceOwner: 'jira-align',
      productName: 'Jira Align',
      emauProductKey: 'jira_align',
      alternatives: {
        productName: ['jira-align', 'Jira Align', 'Jira Align New'],
      },
      hamsProductKey: undefined,
      productLogo: 'JiraAlignLogo',
      productIcon: 'JiraAlignIcon',
      productSettingName: 'Jira Align', // Jira Align has its own settings page
      type: UiProductType.JIRA_ALIGN,
      description: AddProductMessages.jiraAlignDescription,
      summaryPoints: [
        AddProductMessages.jiraAlignInfoSummaryOne,
        AddProductMessages.jiraAlignInfoSummaryTwo,
        AddProductMessages.jiraAlignInfoSummaryThree,
      ],
      addProduct: {
        enabled: false,
        productKey: 'jira-align',
        productCard: {
          appCardDescription: appCardMessages.jiraAlignDescription,
          learnMoreUrl: 'https://www.atlassian.com/software/jira/align',
        },
      },
      productId: '4d114aa3-0046-4d74-9033-34bd4ed0f1db',
      offerings: {
        '866f9cb0-ee4b-40e5-ba15-81356eb502b6': {
          productKey: 'jira-align',
          chargeElement: ChargeType.user,
          edition: EditionType.enterprise,
          pricingType: PricingType.PAID,
        },
        'ca17df4e-ba88-48a2-be6d-47e52ef3cae1': {
          productKey: 'jira-align',
          chargeElement: ChargeType.user,
          edition: EditionType.free,
          pricingType: PricingType.FREE,
        },
      },
    },
    'jira-core': {
      ariResourceOwner: 'jira-core',
      productName: 'Jira Work Management',
      type: UiProductType.JIRA_CORE,
      alternatives: {
        type: [UiProductType.JIRA_WORK_MANAGEMENT],
        productName: ['jira-core', 'Jira Core', 'Jira Work Management'],
        userManagementProductId: ['jira-work-management'],
        userManagementCanonicalProductKey: ['jira-work-management'],
      },
      emauProductKey: 'jira_workManagement',
      sandboxProductKey: 'jira-core.ondemand',
      hamsProductKey: undefined,
      productLogo: 'JiraLogo',
      productIcon: 'JiraIcon',
      dcProductIcon: 'JiraDataCenterIcon',
      productSettingName: 'Jira', // All Jira products share the same settings page
      description: AddProductMessages.jiraWorkManagementDescription,
      summaryPoints: [
        AddProductMessages.jiraWorkManagementInfoSummaryOne,
        AddProductMessages.jiraWorkManagementInfoSummaryTwo,
        AddProductMessages.jiraWorkManagementInfoSummaryThree,
      ],
      addProduct: {
        enabled: true,
        includeWithJWMForMigrationFlow: true,
        productCard: {
          summaryTitle: AddProductMessages.jiraWorkManagementInfoTitle,
          appCardDescription: appCardMessages.jiraWorkManagementDescription,
          learnMoreUrl: 'https://www.atlassian.com/software/jira/work-management',
        },
        productKey: 'jira-core.ondemand',
      },
      links: {
        productLink: ({ productUrl }) => `https://${productUrl}/secure/Dashboard.jspa?source=admin_hub`,
        settingsLink: ({ productUrl }) => {
          const useNewSettingsPage = AkFeatureGates.checkGate('mc-adminhub-product-config-jira-links');

          return useNewSettingsPage
            ? `https://${productUrl}/jira/settings/system/general-configuration?source=admin_hub`
            : `https://${productUrl}/secure/admin/ViewApplicationProperties.jspa?source=admin_hub`;
        },
        manageAppsLink: ({ productUrl }) => `https://${productUrl}/plugins/servlet/upm?source=admin_hub`,
      },
      signUpLink: 'https://www.atlassian.com/try/cloud/signup?bundle=jira-work-management',
      productId: '46ccaa9e-cdb0-4995-8886-5827a5ff162b',
      offerings: {
        '5ab35ef5-e441-493a-b6e0-ba2d21a72ac6': {
          productKey: 'jira-core.ondemand',
          chargeElement: ChargeType.user,
          edition: EditionType.standard,
          pricingType: PricingType.PAID,
        },
      },
    },
    'jira-customer-service': {
      ariResourceOwner: 'jira-customer-service',
      productName: 'Customer Service Management',
      emauProductKey: 'jira_customerService',
      hamsProductKey: undefined,
      productSettingName: 'Jira', // All Jira products share the same settings page
      type: UiProductType.JIRA_CUSTOMER_SERVICE,
      alternatives: {
        productName: ['jira-customer-service', 'Customer Service Management', 'Jira Customer Service'],
      },
      links: {
        productLink: ({ productUrl }) =>
          `https://${productUrl}/secure/BrowseProjects.jspa?page=1&selectedCategory=all&selectedProjectType=customer_service&sortKey=name&sortOrder=ASC&source=admin_hub`,
        settingsLink: ({ productUrl }) => {
          const useNewSettingsPage = AkFeatureGates.checkGate('mc-adminhub-product-config-jira-links');

          return useNewSettingsPage
            ? `https://${productUrl}/jira/settings/system/general-configuration?source=admin_hub`
            : `https://${productUrl}/secure/admin/ViewApplicationProperties.jspa?source=admin_hub`;
        },
        manageAppsLink: ({ productUrl }) => `https://${productUrl}/plugins/servlet/upm?source=admin_hub`,
      },
      productIcon: 'CustomerServiceManagementIcon',
      productLogo: 'CustomerServiceManagementLogo',
      description: AddProductMessages.jiraCustomerServiceDescription,
      summaryPoints: [
        AddProductMessages.jiraCustomerServiceInfoSummaryOne,
        AddProductMessages.jiraCustomerServiceInfoSummaryTwo,
        AddProductMessages.jiraCustomerServiceInfoSummaryThree,
      ],
      addProduct: {
        enabled: true,
        parentCollectionName: CollectionName.ServiceCollection,
        welcomePath: 'secure/WelcomeToSD.jspa?decorator=jsd-onboarding&templateId=jcs.customer-service-management',
        productCard: {
          summaryTitle: AddProductMessages.jiraCustomerServiceInfoTitle,
          appCardDescription: appCardMessages.jiraCustomerServiceDescription,
          learnMoreUrl: 'https://www.atlassian.com/software/jira/service-management/customer-service-software',
        },
        productKey: 'jira-customer-service',
      },
      productId: 'fbc7e14b-2f9c-45a9-9bcb-3993a4eac0ce',
      offerings: {
        '765ce359-df4d-4504-8ffa-c2e7f1bd25e2': {
          productKey: 'jira-customer-service',
          chargeElement: ChargeType.agent,
          edition: EditionType.free,
          pricingType: PricingType.FREE,
        },
        '8fdf76c1-755a-473f-88ce-7944504b24cf': {
          productKey: 'jira-customer-service',
          chargeElement: ChargeType.agent,
          edition: EditionType.enterprise,
          pricingType: PricingType.FREE,
          parentOfferingId: 'c1b6f7ce-21f9-4e85-b136-91f4c484b95d',
        },
      },
    },
    'jira-dc.jira-dc': {
      ariResourceOwner: 'jira-dc.jira-dc',
      productName: thirdPartyAppMessages.jiraDcDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.jiraDcDisplayName,
      connectorType: 'jira-dc-connector',
      productIcon: 'JiraIcon',
    },
    'jira-family': {
      ariResourceOwner: 'jira-family',
      productName: 'Jira Apps',
      idOrgManagerProductKey: undefined,
      userManagementProductId: 'jira-family',
      userManagementCanonicalProductKey: undefined,
      alternatives: {
        productName: ['jira-family'],
      },
      emauProductKey: undefined,
      sandboxProductKey: undefined,
      hamsProductKey: undefined,
      productLogo: 'JiraLogo',
      productIcon: 'JiraIcon',
      dcProductIcon: 'JiraDataCenterIcon',
    },
    'jira-incident-manager': {
      ariResourceOwner: 'jira-incident-manager',
      productName: 'Jira Ops',
      idOrgManagerProductKey: 'jira-ops',
      alternatives: {
        productName: ['jira-ops'],
      },
      emauProductKey: 'jira_ops',
      hamsProductKey: undefined,
      productIcon: 'JiraIcon',
      productLogo: 'JiraLogo',
      productSettingName: 'Jira', // All Jira products share the same settings page
      links: {
        productLink: ({ productUrl }) => `https://${productUrl}/secure/Dashboard.jspa?source=admin_hub`,
        settingsLink: ({ productUrl }) => {
          const useNewSettingsPage = AkFeatureGates.checkGate('mc-adminhub-product-config-jira-links');

          return useNewSettingsPage
            ? `https://${productUrl}/jira/settings/system/general-configuration?source=admin_hub`
            : `https://${productUrl}/secure/admin/ViewApplicationProperties.jspa?source=admin_hub`;
        },
        manageAppsLink: ({ productUrl }) => `https://${productUrl}/plugins/servlet/upm?source=admin_hub`,
      },
    },
    'jira-product-discovery': {
      ariResourceOwner: 'jira-product-discovery',
      productName: 'Jira Product Discovery',
      alternatives: {
        productName: ['jira-product-discovery'],
      },
      emauProductKey: 'jira_productDiscovery',
      hamsProductKey: 'jira-product-discovery',
      enterpriseProductKey: 'com.atlassian.jira-product-discovery',
      productLogo: 'JiraProductDiscoveryLogo',
      productIcon: 'JiraProductDiscoveryIcon',
      productSettingName: 'Jira', // All Jira products share the same settings page
      type: UiProductType.JIRA_PRODUCT_DISCOVERY,
      description: AddProductMessages.jiraProductDiscoveryDescription,
      summaryPoints: [
        AddProductMessages.jiraProductDiscoveryInfoSummaryOne,
        AddProductMessages.jiraProductDiscoveryInfoSummaryTwo,
        AddProductMessages.jiraProductDiscoveryInfoSummaryThree,
      ],
      addProduct: {
        enabled: true,
        productKey: 'jira-product-discovery',
        includeWithExistingSites: true,
        productCard: {
          summaryTitle: AddProductMessages.jiraProductDiscoveryInfoTitle,
          appCardDescription: appCardMessages.jiraProductDiscoveryDescription,
          learnMoreUrl: 'https://www.atlassian.com/software/jira/product-discovery',
        },
      },
      links: {
        productLink: ({ productUrl }) =>
          `https://${productUrl}/secure/BrowseProjects.jspa?page=1&selectedCategory=all&selectedProjectType=product_discovery&sortKey=name&sortOrder=ASC&source=admin_hub`,
        settingsLink: ({ productUrl }) => {
          const useNewSettingsPage = AkFeatureGates.checkGate('mc-adminhub-product-config-jira-links');

          return useNewSettingsPage
            ? `https://${productUrl}/jira/settings/system/general-configuration?source=admin_hub`
            : `https://${productUrl}/secure/admin/ViewApplicationProperties.jspa?source=admin_hub`;
        },
        manageAppsLink: ({ productUrl }) => `https://${productUrl}/plugins/servlet/upm?source=admin_hub`,
      },
      productId: 'a6f908c8-709c-45f4-9e2d-eca25b93b94a',
      offerings: {
        '63592312-d7c5-47b8-9220-923da00a943c': {
          productKey: 'jira-product-discovery',
          chargeElement: ChargeType.user,
          edition: EditionType.enterprise,
          pricingType: PricingType.FREE,
        },
        '35b9268e-66e2-45fb-81d3-dacd1e31831a': {
          productKey: 'jira-product-discovery',
          chargeElement: ChargeType.user,
          edition: EditionType.standard,
          pricingType: PricingType.PAID,
        },
      },
    },
    'jira-servicedesk': {
      ariResourceOwner: 'jira-servicedesk',
      type: UiProductType.JIRA_SERVICE_DESK,
      productName: 'Jira Service Management',
      userManagementProductId: 'jira-servicedesk',
      userManagementCanonicalProductKey: 'jira-servicedesk',
      idOrgManagerProductKey: 'jira-service-desk',
      enterpriseProductKey: 'com.atlassian.enterprise-jira-servicedesk',
      productId: 'de63a248-1f93-46ed-b6ea-ab8e38af1c88',
      alternatives: {
        type: [UiProductType.JIRA_SERVICE_DESK, UiProductType.JIRA_SERVICE_MANAGEMENT],
        productName: ['jira-service-desk', 'jira-servicedesk', 'Jira Service Desk', 'Jira Service Desk Legacy'],
        idOrgManagerProductKey: ['jira-servicedesk', 'jira-servicedesk.ondemand'],
        userManagementProductId: ['jira-service-desk', 'jira-servicedesk.ondemand', 'jira-service-management'],
        userManagementCanonicalProductKey: ['jira-service-desk', 'jira-servicedesk.ondemand', 'jira-service-management'],
        productId: ['08d4e010-47e9-4552-98bc-1d9e5f0fed45'],
      },
      emauProductKey: 'jira_serviceManagement',
      sandboxProductKey: 'jira-servicedesk.ondemand',
      hamsProductKey: 'jira-servicedesk.ondemand',
      productLogo: 'JiraServiceManagementLogo',
      productIcon: 'JiraServiceManagementIcon',
      dcProductIcon: 'JiraServiceManagementDataCenterIcon',
      productSettingName: 'Jira', // All Jira products share the same settings page
      links: {
        productLink: ({ productUrl }) =>
          `https://${productUrl}/secure/BrowseProjects.jspa?page=1&selectedCategory=all&selectedProjectType=service_desk&sortKey=name&sortOrder=ASC&source=admin_hub`,
        settingsLink: ({ productUrl }) => {
          const useNewSettingsPage = AkFeatureGates.checkGate('mc-adminhub-product-config-jira-links');

          return useNewSettingsPage
            ? `https://${productUrl}/jira/settings/system/general-configuration?source=admin_hub`
            : `https://${productUrl}/secure/admin/ViewApplicationProperties.jspa?source=admin_hub`;
        },
        manageAppsLink: ({ productUrl }) => `https://${productUrl}/plugins/servlet/upm?source=admin_hub`,
      },
      signUpLink: 'https://www.atlassian.com/try/cloud/signup?bundle=jira-service-management',
      description: AddProductMessages.jiraServiceManagementDescription,
      summaryPoints: [
        AddProductMessages.jiraServiceManagementInfoSummaryOne,
        AddProductMessages.jiraServiceManagementInfoSummaryTwo,
        AddProductMessages.jiraServiceManagementInfoSummaryThree,
      ],
      addProduct: {
        enabled: true,
        includeWithJSM: true,
        parentCollectionName: CollectionName.ServiceCollection,
        welcomePath: 'secure/WelcomeToSD.jspa?decorator=jsd-onboarding',
        productCard: {
          summaryTitle: AddProductMessages.jiraServiceManagementInfoTitle,
          appCardDescription: appCardMessages.jiraServiceManagementDescription,
          learnMoreUrl: 'https://www.atlassian.com/software/jira/service-desk',
        },
        productKey: 'jira-servicedesk.ondemand',
      },
      offerings: {
        '4021727f-e6d1-47ad-be64-2367755f5c51': {
          productKey: 'jira-servicedesk.ondemand',
          chargeElement: ChargeType.agent,
          edition: EditionType.enterprise,
          pricingType: PricingType.FREE,
        },
        '396efb16-749b-41fa-9cd1-ad10cfefa1d0': {
          productKey: 'jira-servicedesk.ondemand',
          chargeElement: ChargeType.agent,
          edition: EditionType.standard,
          pricingType: PricingType.PAID,
        },
        '06c048ae-08b1-423f-961b-77534c45299a': {
          productKey: 'jira-servicedesk.ondemand',
          chargeElement: ChargeType.agent,
          edition: EditionType.enterprise,
          pricingType: PricingType.FREE,
          parentOfferingId: 'c1b6f7ce-21f9-4e85-b136-91f4c484b95d',
        },
        '10f380ca-bc37-4f5f-b471-ce310c65525a': {
          productKey: 'jira-servicedesk.ondemand',
          chargeElement: ChargeType.agent,
          edition: EditionType.enterprise,
          pricingType: PricingType.FREE,
          parentOfferingId: '66d7a304-7988-41a7-9eea-50f17fdc76c1',
          environment: 'fedramp',
        },
        '441693e6-3329-4ca3-b2d6-d832da7f3566': {
          productKey: 'jira-servicedesk.ondemand',
          chargeElement: ChargeType.agent,
          edition: EditionType.enterprise,
          pricingType: PricingType.FREE,
          environment: 'fedramp',
        },
        '2b89fa74-805c-4914-a3ac-dc4399862e1c': {
          productKey: 'jira-servicedesk.ondemand',
          chargeElement: ChargeType.agent,
          edition: EditionType.enterprise,
          pricingType: PricingType.FREE,
          parentOfferingId: '6af1560d-88b3-4d8c-9d95-ffbef49cb73b',
          environment: 'ic',
        },
      },
    },
    'jira-software': {
      ariResourceOwner: 'jira-software',
      productName: 'Jira',
      emauProductKey: 'jira_software',
      sandboxProductKey: 'jira-software.ondemand',
      hamsProductKey: 'jira-software.ondemand',
      enterpriseProductKey: 'com.atlassian.enterprise-jira-software',
      alternatives: {
        productName: ['jira', 'jira-software', 'Jira Software'],
        idOrgManagerProductKey: ['jira-software.ondemand', 'jira'],
        userManagementProductId: ['jira-software.ondemand', 'com.atlassian.jira'],
        userManagementCanonicalProductKey: ['jira-software.ondemand'],
      },
      productSettingName: 'Jira', // All Jira products share the same settings page
      type: UiProductType.JIRA_SOFTWARE,
      links: {
        productLink: ({ productUrl }) => `https://${productUrl}/secure/Dashboard.jspa?source=admin_hub`,
        settingsLink: ({ productUrl }) => {
          const useNewSettingsPage = AkFeatureGates.checkGate('mc-adminhub-product-config-jira-links');

          return useNewSettingsPage
            ? `https://${productUrl}/jira/settings/system/general-configuration?source=admin_hub`
            : `https://${productUrl}/secure/admin/ViewApplicationProperties.jspa?source=admin_hub`;
        },
        manageAppsLink: ({ productUrl }) => `https://${productUrl}/plugins/servlet/upm?source=admin_hub`,
      },
      signUpLink: 'https://www.atlassian.com/try/cloud/signup?bundle=jira-software',
      productLogo: 'JiraLogo',
      productIcon: 'JiraIcon',
      dcProductIcon: 'JiraDataCenterIcon',
      description: AddProductMessages.jiraDescription,
      summaryPoints: [
        AddProductMessages.jiraInfoSummaryOne,
        AddProductMessages.jiraInfoSummaryTwo,
        AddProductMessages.jiraInfoSummaryThree,
      ],
      addProduct: {
        enabled: true,
        includeWithTWCEnterprise: true,
        productKey: 'jira-software.ondemand',
        parentCollectionName: CollectionName.TeamworkCollection,
        productCard: {
          learnMoreUrl: 'https://www.atlassian.com/software/jira',
          summaryTitle: AddProductMessages.jiraInfoTitle,
          appCardDescription: appCardMessages.jiraDescription,
        },
        welcomePath: 'welcome/software?src=signup&admin=true',
      },
      productId: 'd8a847a4-cde4-4c50-8ea1-dc3d4193214f',
      offerings: {
        '799958b5-36bb-4a3d-9289-9fd2986352a7': {
          productKey: 'jira-software.ondemand',
          chargeElement: ChargeType.user,
          edition: EditionType.enterprise,
          pricingType: PricingType.FREE,
        },
        'a70b5cbb-1ae1-4003-8f4a-9001a4a50526': {
          productKey: 'jira-software.ondemand',
          chargeElement: ChargeType.user,
          edition: EditionType.standard,
          pricingType: PricingType.PAID,
        },
        '80c66c94-a9a4-4041-ab3e-6dd21b018ba0': {
          productKey: 'jira-software.ondemand',
          chargeElement: ChargeType.user,
          edition: EditionType.enterprise,
          pricingType: PricingType.FREE,
          parentOfferingId: 'cf0b0103-2f08-4c87-97cf-a69448d6e69a',
        },
        'b9015f34-200f-4d57-be87-b9025361b78a': {
          productKey: 'jira-software.ondemand',
          chargeElement: ChargeType.user,
          edition: EditionType.enterprise,
          pricingType: PricingType.FREE,
          environment: 'fedramp',
        },
        'c11b6836-96a5-47cf-a796-7d82e1b1fa55': {
          productKey: 'jira-software.ondemand',
          chargeElement: ChargeType.user,
          edition: EditionType.enterprise,
          pricingType: PricingType.FREE,
          environment: 'ic',
        },
      },
    },
    loom: {
      ariResourceOwner: 'loom',
      productName: 'Loom',
      alternatives: {
        productName: ['loom'],
      },
      hamsProductKey: 'loom',
      productLogo: 'LoomLogo',
      productIcon: 'LoomIcon',
      type: UiProductType.LOOM,
      description: AddProductMessages.loomDescription,
      summaryPoints: [
        AddProductMessages.loomInfoSummaryOne,
        AddProductMessages.loomInfoSummaryTwo,
        AddProductMessages.loomInfoSummaryThree,
      ],
      addProduct: {
        enabled: true,
        includeWithTWCEnterprise: true,
        productKey: 'loom',
        parentCollectionName: CollectionName.TeamworkCollection,
        productCard: {
          learnMoreUrl: 'https://www.atlassian.com/software/loom',
          appCardDescription: appCardMessages.loomDescription,
        },
      },
      links: {
        productLink: DEFAULT_PRODUCT_LINK,
        settingsLink: DEFAULT_SETTINGS_LINK,
      },
      productId: '7683a73e-423d-41a1-b43f-d497656f8b5d',
      offerings: {
        '3f72cea3-aa1c-446c-a3e4-d09fed86a62c': {
          productKey: 'loom',
          chargeElement: ChargeType.user,
          edition: EditionType.business_ai,
          pricingType: PricingType.PAID,
        },
        '58250ae3-040d-4f60-9cc0-f6d4e0d10139': {
          productKey: 'loom',
          chargeElement: ChargeType.user,
          edition: EditionType.enterprise,
          pricingType: PricingType.FREE,
          parentOfferingId: 'cf0b0103-2f08-4c87-97cf-a69448d6e69a',
        },
      },
    },
    'loom-attribution': {
      ariResourceOwner: 'loom-attribution',
      productName: 'Loom Attribution',
      userManagementProductId: 'loom-attribution',
      idOrgManagerProductKey: undefined,
      alternatives: {
        productName: ['loom-attribution'],
      },
      emauProductKey: undefined,
      userManagementCanonicalProductKey: undefined,
      sandboxProductKey: undefined,
      hamsProductKey: undefined,
      productLogo: 'LoomAttributionLogo',
      productIcon: 'LoomAttributionIcon',
    },
    'lucid.lucidchart': {
      ariResourceOwner: 'lucid.lucidchart',
      productName: thirdPartyAppMessages.lucidDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.lucidDisplayName,
      connectorType: 'lucid-connector',
      productIcon: 'lucidIcon',
    },
    mercury: {
      ariResourceOwner: 'mercury',
      productName: 'Focus',
      alternatives: {
        productName: ['mercury', 'Mercury'],
        idOrgManagerProductKey: ['focus'],
      },
      emauProductKey: undefined,
      hamsProductKey: undefined,
      productIcon: 'FocusIcon',
      productLogo: 'FocusLogo',
      links: {
        productLink: ({ productUrl }) => `https://${productUrl}/for-you`,
        settingsLink: ({ productUrl }) => `https://${productUrl}/settings`,
      },
      productId: 'd16a2ba8-445f-4e83-9c7b-9bcf349ff905',
    },
    'microsoft.outlook': {
      ariResourceOwner: 'microsoft.outlook',
      productName: thirdPartyAppMessages.microsoftOutlookMailDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.microsoftOutlookMailDisplayName,
      connectorType: 'outlook-mail-connector',
      productIcon: 'microsoftOutlookMailIcon',
    },
    'microsoft.outlook-calendar': {
      ariResourceOwner: 'microsoft.outlook-calendar',
      productName: thirdPartyAppMessages.microsoftOutlookCalendarDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.microsoftOutlookCalendarDisplayName,
      connectorType: 'outlook-calendar-connector',
      productIcon: 'microsoftOutlookCalendarIcon',
    },
    'microsoft.sharepoint': {
      ariResourceOwner: 'microsoft.sharepoint',
      productName: thirdPartyAppMessages.microsoftSharepointDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.microsoftSharepointDisplayName,
      connectorType: 'platform-sharepoint-connector',
      productIcon: 'microsoftSharepointIcon',
    },
    'microsoft.teams': {
      ariResourceOwner: 'microsoft.teams',
      productName: thirdPartyAppMessages.microsoftTeamsDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.microsoftTeamsDisplayName,
      connectorType: 'microsoft-teams-connector',
      productIcon: 'microsoftTeamsIcon',
    },
    'miro.miro': {
      ariResourceOwner: 'miro.miro',
      productName: thirdPartyAppMessages.miroDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.miroDisplayName,
      connectorType: 'miro-connector',
      productIcon: 'miroIcon',
    },
    'monday.monday': {
      ariResourceOwner: 'monday.monday',
      productName: thirdPartyAppMessages.mondayDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.mondayDisplayName,
      connectorType: 'monday-connector',
      productIcon: 'mondayIcon',
    },
    'newrelic.newrelic': {
      ariResourceOwner: 'newrelic.newrelic',
      productName: thirdPartyAppMessages.newRelicDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.newRelicDisplayName,
      connectorType: 'newrelic-connector',
      productIcon: 'newrelicIcon',
    },
    'notion.notion': {
      ariResourceOwner: 'notion.notion',
      productName: thirdPartyAppMessages.notionDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.notionDisplayName,
      connectorType: 'notion-connector',
      productIcon: 'notionIcon',
    },
    opsgenie: {
      ariResourceOwner: 'opsgenie',
      productName: 'Opsgenie',
      alternatives: {
        productName: ['opsgenie'],
      },
      hamsProductKey: undefined,
      productLogo: 'OpsgenieLogo',
      productIcon: 'OpsgenieIcon',
      links: {
        productLink: DEFAULT_PRODUCT_LINK,
        settingsLink: DEFAULT_SETTINGS_LINK,
      },
      signUpLink: 'https://www.atlassian.com/try/cloud/signup?bundle=opsgenie',
      productId: 'fdb4649d-ca4d-49f8-a8bf-ffa558a65551',
    },
    passionfruit: {
      ariResourceOwner: 'passionfruit',
      productName: 'Passionfruit',
      alternatives: {
        productName: ['passionfruit'],
      },
      hamsProductKey: undefined,
      type: UiProductType.PASSIONFRUIT,
      description: AddProductMessages.emptyDescription,
      summaryPoints: [],
      addProduct: {
        enabled: false,
        productKey: 'passionfruit',
      },
      links: {
        productLink: DEFAULT_PRODUCT_LINK,
        settingsLink: DEFAULT_SETTINGS_LINK,
      },
      productId: '2b8ea083-125a-49b1-bfdb-d6ae767fe08a',
      offerings: {
        'd0fb44b9-624d-4f0e-8219-3ebea71c459e': {
          productKey: 'passionfruit',
          chargeElement: ChargeType.user,
          edition: EditionType.free,
          pricingType: PricingType.FREE,
        },
      },
    },
    project: {
      ariResourceOwner: 'project',
      productName: 'Projects',
      type: UiProductType.PROJECT,
      alternatives: {
        productName: ['Project'],
      },
      hamsProductKey: undefined,
      productSettingName: undefined, // AdminHub is the settings page for Projects
      links: {
        productLink: ({ homeUrl, orgId, siteId }) => `${homeUrl}/o/${orgId}/s/${siteId}/projects`,
        settingsLink: ({ homeUrl, orgId, siteId }) => {
          const projectSettingsLinkSuffix = AkFeatureGates.checkGate('atlas_projects_app_enabled_adminhub')
            ? 'projects/settings'
            : 'settings/workspace';

          return `${homeUrl}/o/${orgId}/s/${siteId}/${projectSettingsLinkSuffix}`;
        },
      },
      productIcon: 'ProjectsIcon',
      productLogo: 'ProjectsLogo',
      productId: 'ad341b0e-fc59-44bf-8b95-46bda5e89e28',
    },
    radar: {
      ariResourceOwner: 'radar',
      productName: 'Talent',
      alternatives: {
        productName: ['radar', 'Radar', 'radar.ondemand'],
        idOrgManagerProductKey: ['radar.ondemand'],
        userManagementCanonicalProductKey: ['radar.ondemand'],
        userManagementProductId: ['radar.ondemand'],
      },
      productIcon: 'TalentIcon',
      productLogo: 'TalentLogo',
      hamsProductKey: undefined,
      sandboxProductKey: 'radar.ondemand',
      links: {
        productLink: ({ productUrl }) => `https://${productUrl}/for-you`,
        settingsLink: ({ productUrl }) => `https://${productUrl}/settings`,
      },
      productId: '2027fadc-c46f-4835-a07f-f3f716d8827f',
    },
    rovo: {
      ariResourceOwner: 'rovo',
      productName: 'Rovo',
      sandboxProductKey: 'rovo.ondemand',
      hamsProductKey: undefined,
      alternatives: {
        productName: ['rovo', 'Rovo', 'rovo.ondemand'],
        idOrgManagerProductKey: ['rovo.ondemand'],
        userManagementProductId: ['rovo.ondemand'],
        userManagementCanonicalProductKey: ['rovo.ondemand'],
      },
      // TODO: Handle Rovo Links
      links: {
        productLink: ({ siteId }) => `https://rovo.atlassian.com?cloudId=${siteId}`,
        settingsLink: ({ orgId }) => `/o/${orgId}/rovo-settings`,
      },
      productLogo: 'RovoLogo',
      productIcon: 'RovoIcon',
      type: UiProductType.ROVO,
      addProduct: {
        enabled: false,
        productKey: 'rovo.ondemand',
        includeWithRovoPaidProvisioning: true,
      },
      productId: '91ae3e3f-138e-43be-98cc-48df03d13c32',
    },
    'salesforce.salesforce': {
      ariResourceOwner: 'salesforce.salesforce',
      productName: thirdPartyAppMessages.salesforceDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.salesforceDisplayName,
      connectorType: 'salesforce-connector',
      productIcon: 'salesforceIcon',
    },
    'servicenow.servicenow': {
      ariResourceOwner: 'servicenow.servicenow',
      productName: thirdPartyAppMessages.serviceNowDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.serviceNowDisplayName,
      connectorType: 'servicenow-connector',
      productIcon: 'serviceNowIcon',
    },
    slack: {
      ariResourceOwner: 'slack',
      productName: thirdPartyAppMessages.slackDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.slackDisplayName,
      connectorType: 'slack-connector',
      productIcon: 'slackIcon',
    },
    'smartsheet.smartsheet': {
      ariResourceOwner: 'smartsheet.smartsheet',
      productName: thirdPartyAppMessages.smartsheetDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.smartsheetDisplayName,
      connectorType: 'smartsheet-connector',
      productIcon: 'smartsheetIcon',
    },
    'spinnaker.spinnaker': {
      ariResourceOwner: 'spinnaker.spinnaker',
      productName: thirdPartyAppMessages.spinnakerDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.spinnakerDisplayName,
      connectorType: 'spinnaker-connector',
      productIcon: 'spinnakerIcon',
    },
    statuspage: {
      ariResourceOwner: 'statuspage',
      productName: 'Statuspage',
      emauProductKey: 'statuspage_manage',
      hamsProductKey: undefined,
      productLogo: 'StatuspageLogo',
      productIcon: 'StatuspageIcon',
      type: UiProductType.STATUSPAGE,
      links: {
        productLink: DEFAULT_PRODUCT_LINK,
        settingsLink: DEFAULT_SETTINGS_LINK,
      },
      signUpLink: 'https://www.atlassian.com/try/cloud/signup?bundle=statuspage',
      description: AddProductMessages.statusPageDescription,
      summaryPoints: [
        AddProductMessages.statuspageInfoSummaryOne,
        AddProductMessages.statuspageInfoSummaryTwo,
        AddProductMessages.statuspageInfoSummaryThree,
      ],
      addProduct: {
        enabled: true,
        productKey: 'statuspage',
        productCard: {
          learnMoreUrl: 'https://www.atlassian.com/software/statuspage',
          summaryTitle: AddProductMessages.statuspageInfoTitle,
          appCardDescription: appCardMessages.statusPageDescription,
        },
      },
      productId: 'fb892fd7-2de3-4a89-8583-49de8e7b7f55',
      offerings: {
        'cff16c60-539b-11ea-8d77-2e728ce88125': {
          productKey: 'statuspage',
          chargeElement: ChargeType.user,
          edition: EditionType.free,
          pricingType: PricingType.FREE,
        },
      },
    },
    studio: {
      ariResourceOwner: 'studio',
      ariResourceType: 'workspace',
      productName: 'Studio',
      hamsProductKey: undefined,
      productIcon: 'StudioIcon',
      productLogo: 'StudioLogo',
      links: {
        productLink: ({ productUrl, studioWorkspaceId, email }) => {
          if (email && AkFeatureGates.checkGate('asf-944-account-sync')) {
            const url = new URL(`https://${productUrl}/w/${studioWorkspaceId}`);
            url.searchParams.set('login_hint', email);
            return url.toString();
          }
          return `https://${productUrl}/w/${studioWorkspaceId}`;
        },
        settingsLink: ({ productUrl, studioWorkspaceId, email }) => {
          if (email && AkFeatureGates.checkGate('asf-944-account-sync')) {
            const url = new URL(`https://${productUrl}/w/${studioWorkspaceId}/settings/admin`);
            url.searchParams.set('login_hint', email);
            return url.toString();
          }
          return `https://${productUrl}/w/${studioWorkspaceId}/settings/admin`;
        },
      },
    },
    teams: {
      ariResourceOwner: 'teams',
      ariResourceType: 'workspace',
      productName: 'Teams',
      alternatives: {
        productName: ['teams', 'Teams'],
      },
      hamsProductKey: undefined,
      productIcon: 'TeamsIcon',
      productLogo: 'TeamsLogo',
    },
    townsquare: {
      ariResourceOwner: 'townsquare',
      type: UiProductType.TEAMCENTRAL,
      productName: 'Atlas',
      alternatives: {
        productName: ['townsquare', 'Team Central'],
        idOrgManagerProductKey: ['atlas'],
      },
      userManagementProductId: 'townsquare',
      idOrgManagerProductKey: 'townsquare',
      emauProductKey: 'atlas',
      userManagementCanonicalProductKey: 'townsquare',
      sandboxProductKey: 'townsquare',
      hamsProductKey: undefined,
      links: {
        productLink: ({ productUrl }) => `https://${productUrl}/?source=admin_hub`,
        settingsLink: DEFAULT_SETTINGS_LINK,
      },
      productId: '7c917705-866d-4b6b-b266-0fc7633e8342',
    },
    trello: {
      ariResourceOwner: 'trello',
      ariResourceType: 'enterprise',
      productName: 'Trello',
      hamsProductKey: undefined,
      alternatives: {
        productName: ['trello', 'trello.free'],
        idOrgManagerProductKey: ['trello.free'],
      },
      // ProductConfig does not support Trello URLs. This is a temporary workaround. Do not follow this example.
      links: {
        productLink: ({ productUrl }) =>
          productUrl && productUrl.startsWith('trellis.coffee')
            ? `https://trellis.coffee/ensureSession?returnUrl=${encodeURIComponent('https://trellis.coffee')}`
            : `https://trello.com/ensureSession?returnUrl=${encodeURIComponent('https://trello.com')}`,
        settingsLink: ({ productUrl }) =>
          `https://${productUrl}`.startsWith('https://trellis.coffee')
            ? `https://trellis.coffee/ensureSession?returnUrl=${encodeURIComponent(`https://${productUrl}`)}`
            : `https://trello.com/ensureSession?returnUrl=${encodeURIComponent(`https://${productUrl}`)}`,
      },
      productLogo: 'TrelloLogo',
      productIcon: 'TrelloIcon',
      productId: '196be9d6-d01b-4eef-ac43-6a806ef56aa6',
    },
    'unified-help': {
      ariResourceOwner: 'unified-help',
      productName: 'Unified Help',
      alternatives: {
        productName: ['unified-help'],
      },
      emauProductKey: undefined,
      hamsProductKey: undefined,
      links: {
        productLink: DEFAULT_PRODUCT_LINK,
        settingsLink: DEFAULT_SETTINGS_LINK,
      },
      productId: '330b7c44-74df-42e2-98a6-30bf0fbd098b',
    },
    'unknown-atlassian': {
      ariResourceOwner: 'unknown-atlassian',
      productName: 'Unknown Atlassian app',
      userManagementProductId: 'unknown-atlassian',
      idOrgManagerProductKey: 'unknown-atlassian',
      userManagementCanonicalProductKey: undefined,
      emauProductKey: undefined,
      sandboxProductKey: undefined,
      hamsProductKey: undefined,
    },
    workday: {
      ariResourceOwner: 'workday',
      productName: thirdPartyAppMessages.workdayDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.workdayDisplayName,
      connectorType: 'workday-connector',
      productIcon: 'workdayIcon',
    },
    'workday-v2': {
      ariResourceOwner: 'workday-v2',
      // productName stays as 'Workday V2' — this is the internal/non-localized key
      // used for the reverse mapping in buildReverseMappings(),
      // so it must remain unique to avoid the duplicate error.
      productName: thirdPartyAppMessages.workdayV2DisplayName.defaultMessage,
      // localizedProductName is kept as  'Workday' — this is what the UI actually displays.
      // All UI components check localizedProductName first
      // only fall back to productName if it's not set.
      localizedProductName: thirdPartyAppMessages.workdayDisplayName,
      connectorType: 'workday-v2-connector',
      productIcon: 'workdayIcon',
    },
    'zendesk.zendesk': {
      ariResourceOwner: 'zendesk.zendesk',
      productName: thirdPartyAppMessages.zendeskDisplayName.defaultMessage,
      localizedProductName: thirdPartyAppMessages.zendeskDisplayName,
      connectorType: 'zendesk-connector',
      productIcon: 'zendeskIcon',
    },
  },
  productOverrides: {
    align_adminhub_ui: {
      'jira-align': {
        ariResourceOwner: 'jira-align',
        productName: 'Jira Align',
        addProduct: {
          enabled: true,
          productKey: 'jira-align',
          productCard: {
            learnMoreUrl: 'https://www.atlassian.com/software/jira-align',
          },
        },
      },
    },
    feedback_adminhub_ui: {
      feedback: {
        ariResourceOwner: 'feedback',
        productName: 'Feedback',
        type: UiProductType.FEEDBACK,
        productLogo: 'FeedbackLogo',
        productIcon: 'FeedbackIcon',
        hamsProductKey: undefined,
        alternatives: {
          productName: ['feedback', 'Feedback'],
        },
        links: {
          productLink: DEFAULT_PRODUCT_LINK,
          settingsLink: DEFAULT_SETTINGS_LINK,
        },
        productId: '5d69bf62-f4e9-437a-9756-4ffa5f82b46e',
        offerings: {
          '70ff6da2-23ea-4374-b4da-89bb0b939584': {
            productKey: 'feedback',
            chargeElement: ChargeType.user,
            edition: EditionType.free,
            pricingType: PricingType.FREE,
          },
        },
        description: appCardMessages.feedbackDescription,
        summaryPoints: [],
        addProduct: {
          enabled: true,
          includeWithExistingSites: true,
          productKey: 'feedback',
          productCard: {
            learnMoreUrl: 'https://www.atlassian.com/software/feedback',
            appCardDescription: appCardMessages.feedbackDescription,
          },
        },
      },
    },
    passionfruit_adminhub_ui: {
      passionfruit: {
        ariResourceOwner: 'passionfruit',
        productName: 'Passionfruit',
        addProduct: {
          enabled: true,
          productKey: 'passionfruit',
          productCard: {
            learnMoreUrl: 'https://www.atlassian.com/software/passionfruit',
          },
        },
      },
    },
  },
};
